import React, { useEffect, useRef } from 'react';
import {
  Route, Routes, useLocation,
} from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import ReactGA from 'react-ga4';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { ToastContainer } from 'react-toastify';
import * as uiActionCreators from './store/actioncreators/uiActionCreators';
import routes from './routes';
import NotFound from './components/Pages/NotFound';
import Login from './components/Pages/Auth/Login';
import Authentication from './components/Pages/Auth/Authentication';
import PaymentConfirmed from './components/Pages/PaymentConfirmed';
import withLayoutManager from './components/HOC/withLayoutManager';
import AccessDenied from './components/Pages/AccessDenied';
import config from './config/config';
import { removeCookie } from './helpers/cookieHelper';
import ShareContentPreview from './components/Pages/ShareContentPreview/ShareContentPreview';
import NewsletterContentPreview from './components/Pages/NewsletterContentPreview/NewsletterContentPreview';
import AppLinkRedirect from './components/Pages/AppLinkRedirect/AppLinkRedirect';

const AppRouter = () => {
  const location = useLocation();
  const cookieName = `CookieConsent_${config.ENV}`;
  const transitionStarted = () => {
    document.body.classList.add('transition');
  };

  const transitionEnded = () => {
    document.body.classList.remove('transition');
  };

  const dispatch = useDispatch();
  const isGAEnabled = () => getCookieConsentValue(cookieName) === 'true';

  const initGA = () => {
    const gaId = config.GA_ID;
    if (gaId && isGAEnabled()) {
      ReactGA.initialize(gaId);
    } else {
      removeCookie('_ga');
      removeCookie('_gat');
      removeCookie('_gid');
    }
  };

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    // GA4 automatically tracks pageviews, so we don't need manual tracking
    // The ReactGA.initialize() call in useEffect already handles this

    // const gaId = config.GA_ID;
    // if (ReactGA && gaId && isGAEnabled()) {
    //   // ReactGA.pageview(window.location.pathname + window.location.search);
    //   ReactGA.send({
    //     hitType: 'pageview',
    //     page: window.location.pathname + window.location.search,
    //     title: document.title,
    //   });
    // }

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    return () => {
      const { setSearchText } = bindActionCreators(uiActionCreators, dispatch);
      setSearchText('');
    };
  }, [location]);

  const currentKey = location.pathname || '/';
  const timeout = { enter: 1000, exit: 0 };
  const nodeRef = useRef(null);

  return (
    <TransitionGroup className="transition-wrapper">
      <CSSTransition
        key={currentKey}
        timeout={timeout}
        classNames="transition-page"
        nodeRef={nodeRef}
        /* mountOnEnter={false}
        unmountOnExit */
        onEnter={transitionStarted}
        onExited={transitionEnded}
      >
        <div ref={nodeRef}>
          <Routes>
            <Route path="/app" element={<AppLinkRedirect />} />
            {routes.map((route: any) => {
              const WrappedComponent = withLayoutManager(route.component);
              return (
                <Route
                  path={route.path}
                  element={<WrappedComponent />}
                  key={route.path}
                />
              );
            })}
            <Route path="/login" element={<Login />} />
            <Route path="/access-denied" element={<AccessDenied />} />
            <Route path="/:school/authenticate/sso" element={<Authentication />} />
            <Route path="/:school/authenticate/:sso" element={<Authentication />} />
            <Route path="/:school/authenticate" element={<Authentication />} />
            <Route path="/payment-confirmed" element={<PaymentConfirmed />} />
            <Route path="/content/:token" element={<ShareContentPreview />} />
            <Route path="/newsletter-article/:id/:hash" element={<NewsletterContentPreview />} />
            {(() => {
              const WrappedNotFound = withLayoutManager(NotFound);
              return <Route path="*" element={<WrappedNotFound />} />;
            })()}
          </Routes>
          {location.pathname !== '/payment-confirmed' && (
            <CookieConsent
              enableDeclineButton
              location="bottom"
              buttonText="Accept"
              declineButtonText="Decline"
              // style={{ backgroundColor: '#1A1430', color: 'white' }}
              // buttonStyle={{ backgroundColor: '#d10027', color: 'white' }}
              buttonClasses="btn-accept"
              declineButtonClasses="btn-decline"
              contentClasses="cookie-body-info"
              buttonWrapperClasses="cookie-action"
              // declineButtonStyle={{ backgroundColor: 'transparent', color: 'white' }}
              cookieName={`CookieConsent_${config.ENV}`}
              onAccept={() => initGA()}
            >
              This website uses cookies to improve your experience.
              We&apos;ll assume you&apos;re ok with this, but you can opt-out if you wish.
            </CookieConsent>
          )}
          <ToastContainer theme="colored" position="bottom-right" />
        </div>
      </CSSTransition>
    </TransitionGroup>
  );
};

export default AppRouter;
